'use client';

import React, { useEffect, useRef } from 'react';
import { useModalProvider } from '@dr-pam/common-components/Components/Modal/ModalProvider';
import UpgradeBanner from './UpgradeBanner';
import { showUpgradeModal } from './UpgradeModal';
import { useUpgradePrompts } from './UpgradePromptContext';

/**
 * Component that renders upgrade prompts (banners and modals).
 * Uses UpgradePromptContext to get prompts and handles display logic.
 *
 * This component should be placed in the app layout to display prompts globally.
 */
export default function UpgradePromptManager() {
	const { activePrompts, recordEngagement, dismissPrompt, markPromptAsShown, isLoading } = useUpgradePrompts();
	const modalProvider = useModalProvider();

	// Track which modals have been shown in this page load to prevent duplicate displays
	const shownModalsRef = useRef<Set<string>>(new Set());

	// Separate banners and modals
	const bannerPrompts = activePrompts.filter((p) => p.prompt.promptType === 'BANNER');
	const modalPrompts = activePrompts.filter((p) => p.prompt.promptType === 'MODAL');

	// Show modals (highest priority first) - wait until loading is complete
	useEffect(() => {
		if (isLoading || modalPrompts.length === 0) {
			return;
		}

		const modalToShow = modalPrompts[0];
		const modalId = modalToShow.prompt.id;
		const { frequency } = modalToShow.prompt;

		if (shownModalsRef.current.has(modalId)) {
			return;
		}

		// TODO: Add isReady check to ModalProvider when available
		// 		ModalProviderValue type only contains: modals, setModalTitle, openModal, closeModal, closeAllModals
		// No isReady property exists in the shared component interface so we cannot check if the modal provider is ready
		// if (!modalProvider.isReady) return;

		// Mark immediately (optimistic)
		shownModalsRef.current.add(modalId);
		if (frequency === 'ONCE' || frequency === 'ONCE_PER_SESSION' || frequency === 'ONCE_PER_DAY') {
			markPromptAsShown(modalId, frequency);
		}

		try {
			showUpgradeModal(modalProvider, modalToShow, recordEngagement);
		} catch (error) {
			console.error('[UpgradePromptManager] Error showing modal:', error);
			shownModalsRef.current.delete(modalId);
			// Rollback sessionStorage if needed
		}
	}, [modalPrompts, modalProvider, recordEngagement, markPromptAsShown, isLoading]);

	// Don't render if loading or no prompts
	if (isLoading || activePrompts.length === 0) {
		return null;
	}

	// Show highest priority banner (or first one if no priority)
	const bannerToShow = bannerPrompts[0];

	return (
		<>
			{bannerToShow && (
				<UpgradeBanner
					prompt={bannerToShow}
					onDismiss={() => dismissPrompt(bannerToShow.prompt.id)}
					onEngage={recordEngagement}
				/>
			)}
		</>
	);
}
